import {createSlice, PayloadAction, isAnyOf} from '@reduxjs/toolkit';

import {hasOwn} from '~shared/utils';
import {NavigationPageType} from '~shared/models';

import {isString, pushUnique} from '~ui/utils';

import {createPage, importPage, createPageWithDetails} from '~ui/store/thunks/pages';

import {
    applyOperation,
    MovePageRevertableOperation,
    revertOperation,
    getNewSlug,
    addChild,
    deletePageNavigation,
} from '~ui/services/navigation-tree';

import {finishPagesMove} from '~ui/store/actions';

import {deletePage} from '../../thunks';

import {loadNavigationTreeNode, getMoreNavigationTreeItems, loadNavigationTree} from './thunks';
import {NavigationEntityState, NavigationItemExpandedState} from './types';

interface NavigationTreeState {
    entities: Record<string, NavigationEntityState>;
    hasNavigation: boolean;
}

const initialState: NavigationTreeState = {
    entities: {},
    hasNavigation: false,
};

interface CreateCopiedPageNavigationPayload {
    parentSlug: string;
    slug: string;
}

export const {actions, reducer} = createSlice({
    name: 'navigation-tree',
    initialState,
    reducers: {
        setExpanded(
            state,
            action: PayloadAction<{slug: string; expandedState: NavigationItemExpandedState}>,
        ) {
            const {slug, expandedState} = action.payload;
            const entity = state.entities[slug];

            if (!entity) return;

            entity.expandedState = expandedState;
        },

        setHasNavigation(state, action: PayloadAction<boolean>) {
            state.hasNavigation = action.payload;
        },

        applyOperation(state, action: PayloadAction<MovePageRevertableOperation>) {
            const {entities} = state;
            const operation = action.payload;

            applyOperation(entities, operation);
        },

        revertOperation(state, action: PayloadAction<MovePageRevertableOperation>) {
            const {entities} = state;
            const operation = action.payload;

            if (!operation) return;

            revertOperation(entities, operation);
        },

        cancelMove(state, action: PayloadAction<MovePageRevertableOperation[]>) {
            const {entities} = state;
            const operations = action.payload;

            for (let i = operations.length - 1; i >= 0; i--) {
                const operationToRevert = operations[i];

                if (!operationToRevert) {
                    continue;
                }

                revertOperation(entities, operationToRevert);
            }
        },

        deletePageNavigation(state, action: PayloadAction<string>) {
            deletePageNavigation(state.entities, action.payload);
        },

        addCopiedPageNavigation(state, action: PayloadAction<CreateCopiedPageNavigationPayload>) {
            const {slug, parentSlug: parent} = action.payload;
            const {entities} = state;
            const parentNavigation = entities[parent];

            if (!parentNavigation) {
                return;
            }

            const copiedPageNavigation = getDefaultItemState(slug);

            entities[slug] = copiedPageNavigation;

            parentNavigation.children.unshift(slug);
        },

        openNavigationTreeNode(state, action: PayloadAction<string>) {
            const {entities} = state;
            const slug = action.payload;

            const page = entities[slug];

            let parentSlug = page?.parent;

            while (parentSlug) {
                const parentPage = entities[parentSlug];

                if (!parentPage) return;

                if (parentPage.expandedState === NavigationItemExpandedState.Closed) {
                    parentPage.expandedState = NavigationItemExpandedState.Expanded;
                }

                parentSlug = parentPage?.parent;
            }
        },
    },
    extraReducers: (builder) =>
        builder
            .addCase(loadNavigationTree.pending, (state, action) => {
                const {parentSlug} = action.meta.arg;

                // completely reload navigation tree
                state.entities = {};

                const entity = state.entities[parentSlug];

                if (!entity) {
                    updateEntity(state, parentSlug, {
                        expandedState: NavigationItemExpandedState.Expanded,
                        loading: true,
                    });

                    return;
                }

                entity.loading = true;
                entity.expandedState = NavigationItemExpandedState.Expanded;
            })
            .addCase(loadNavigationTree.fulfilled, (state, action) => {
                // we do not calculate virtualSlug here, since it's a complete
                // tree load from scratch, and virtualSlug will always be undefined here

                const {parentSlug} = action.meta.arg;
                const {children, breadcrumbs_branch: breadcrumbsBranch} = action.payload;

                const {results, next_cursor: nextCursor, prev_cursor: prevCursor} = children;

                const parent = state.entities[parentSlug];

                // by now this page must be created in `pending` actions
                if (!parent) return;

                parent.nextCursor = nextCursor;
                parent.prevCursor = prevCursor;
                parent.loading = false;

                results.forEach((child) => {
                    pushUnique(parent.children, child.slug);

                    updateEntity(state, child.slug, {
                        hasChildren: child.has_children,
                        type: child.type,
                        parent: parentSlug,
                        isFixed: child.is_fixed,
                    });
                });

                if (!breadcrumbsBranch) return;

                const lastIndex = breadcrumbsBranch.length - 1;
                let prevItem: NavigationEntityState | undefined = undefined;

                breadcrumbsBranch.forEach((child, index) => {
                    if (prevItem) {
                        // we don't push twice the same slug
                        pushUnique(prevItem.children, child.slug);
                    }

                    const existingItem = state.entities[child.slug];
                    // if the tree is rendered starting from the root
                    // the first breadcrumbBranch item will be already in the state
                    // and the parent for it will be set. No need to
                    // overwrite it here with `undefined`
                    const itemParent = existingItem?.parent || prevItem?.slug;

                    let expandedState;

                    if (parent.slug === child.slug) {
                        expandedState = parent.expandedState;
                    } else if (lastIndex === index) {
                        expandedState = NavigationItemExpandedState.Closed;
                    } else {
                        expandedState = NavigationItemExpandedState.NeedsExpand;
                    }

                    updateEntity(state, child.slug, {
                        hasChildren: child.has_children,
                        type: child.type,
                        parent: itemParent,
                        expandedState,
                        isFixed: child.is_fixed,
                    });

                    prevItem = state.entities[child.slug];
                });

                const firstBreadcrumbItem = breadcrumbsBranch[0];
                const firstBreadcrumbItemNavigation = firstBreadcrumbItem
                    ? state.entities[firstBreadcrumbItem.slug]
                    : undefined;

                // only valid case for b2b, when we render tree from the root
                // and the first breadcrumb item is a deleted page.
                // we wouldn't receive it in the `results` array.
                // therefore we must append it's slug to `parent.children` by hand
                if (
                    firstBreadcrumbItemNavigation &&
                    // check that it's b2b tree by comparing first item with `parent`
                    parent.slug !== firstBreadcrumbItemNavigation.slug &&
                    // check that it's deleted page
                    firstBreadcrumbItemNavigation.type === NavigationPageType.Gap
                ) {
                    parent.children.push(firstBreadcrumbItemNavigation.slug);
                    firstBreadcrumbItemNavigation.parent = parent.slug;
                }
            })
            .addCase(loadNavigationTreeNode.pending, (state, action) => {
                const {parentSlug} = action.meta.arg;

                const entity = state.entities[parentSlug];

                if (!entity) {
                    updateEntity(state, parentSlug, {
                        loading: true,
                    });

                    return;
                }

                entity.loading = true;
            })
            .addCase(loadNavigationTreeNode.fulfilled, (state, action) => {
                const {parentSlug} = action.meta.arg;
                const {children} = action.payload;

                const {results, next_cursor: nextCursor, prev_cursor: prevCursor} = children;

                const parent = state.entities[parentSlug];

                // by now this page must be created in `pending` actions
                if (!parent) return;

                // child that is actually saved under the `parent` page
                const firstRealChild = parent.children.find((childSlug) => {
                    const entity = state.entities[childSlug];
                    if (!entity) {
                        return false;
                    }

                    if (!entity.virtualSlug || entity.virtualSlug === entity.slug) {
                        return true;
                    }

                    return false;
                });

                let currentChildren: string[] = [];

                // if we have real child there, we must position all of
                // the virtual children relative to it.
                // there for we rewrite a `parent.children` to an empty array
                // and save current order inside `currentChildren`.
                if (firstRealChild) {
                    currentChildren = [...parent.children];
                    parent.children = [];
                }
                parent.nextCursor = nextCursor;
                parent.prevCursor = prevCursor;
                parent.loading = false;
                parent.expandedState = NavigationItemExpandedState.Expanded;

                const seenChildrenSet = new Set<string>();

                const addChild = (slug: string) => {
                    if (seenChildrenSet.has(slug)) {
                        return;
                    }
                    seenChildrenSet.add(slug);
                    parent.children.push(slug);
                };

                results.forEach((child) => {
                    if (firstRealChild && child.slug === firstRealChild) {
                        // instead of adding only `child.slug` we add all `currentChildren`
                        // to preserve the sequence
                        currentChildren.forEach(addChild);
                    } else {
                        addChild(child.slug);
                    }

                    updateEntity(state, child.slug, {
                        hasChildren: child.has_children,
                        type: child.type,
                        parent: parentSlug,
                        virtualSlug: parent.virtualSlug
                            ? getNewSlug(child.slug, parent.virtualSlug)
                            : undefined,
                        isFixed: child.is_fixed,
                    });
                });
            })
            .addCase(loadNavigationTreeNode.rejected, (state, action) => {
                const {parentSlug} = action.meta.arg;

                const entity = state.entities[parentSlug];

                if (!entity) return;

                entity.loading = false;
            })
            .addCase(getMoreNavigationTreeItems.pending, (state, action) => {
                const {parentSlug, type} = action.meta.arg;
                setLoadingMore(state, {type, parentSlug, value: true});
            })
            .addCase(getMoreNavigationTreeItems.fulfilled, (state, action) => {
                const {parentSlug, type} = action.meta.arg;
                const {results, next_cursor: nextCursor, prev_cursor: prevCursor} = action.payload;

                const entity = state.entities[parentSlug];

                if (!entity) return;
                const childSlugs: string[] = [];

                results.forEach((child) => {
                    childSlugs.push(child.slug);

                    updateEntity(state, child.slug, {
                        hasChildren: child.has_children,
                        type: child.type,
                        parent: parentSlug,
                        virtualSlug: entity.virtualSlug
                            ? getNewSlug(child.slug, entity.virtualSlug)
                            : undefined,
                        isFixed: child.is_fixed,
                    });
                });

                if (type === 'next') {
                    entity.nextCursor = nextCursor;
                    entity.children = entity.children.concat(childSlugs);
                } else {
                    entity.prevCursor = prevCursor;
                    entity.children = childSlugs.concat(entity.children);
                }

                setLoadingMore(state, {type, parentSlug, value: false});
            })
            .addCase(getMoreNavigationTreeItems.rejected, (state, action) => {
                const {parentSlug, type} = action.meta.arg;
                setLoadingMore(state, {type, parentSlug, value: false});
            })
            .addCase(deletePage.fulfilled, (state, action) => {
                const {slug} = action.meta.arg;
                const {entities} = state;
                deletePageNavigation(entities, slug);
            })
            .addCase(finishPagesMove, (state, action) => {
                const {slugsToUpdate} = action.payload;

                const newEntities: Record<string, NavigationEntityState> = {};

                for (const key in state.entities) {
                    const entity = state.entities[key];

                    if (!entity) continue;

                    const newEntitySlug = slugsToUpdate[entity.slug];

                    if (isString(newEntitySlug)) {
                        entity.slug = newEntitySlug;
                    }

                    entity.virtualSlug = undefined;

                    if (entity.parent && hasOwn(slugsToUpdate, entity.parent)) {
                        entity.parent = slugsToUpdate[entity.parent];
                    }

                    entity.children = entity.children.map((childSlug) => {
                        const newChildSlug = slugsToUpdate[childSlug];

                        return newChildSlug || childSlug;
                    });

                    newEntities[entity.slug] = entity;
                }

                state.entities = newEntities;
            })
            .addCase(createPageWithDetails.fulfilled, (state, action) => {
                const {slug} = action.meta.arg;
                const newPage = getDefaultItemState(slug);

                updateEntity(state, slug, newPage);
            })
            .addMatcher(isAnyOf(createPage.fulfilled, importPage.fulfilled), (state, action) => {
                const {slug, parentSlug} = action.meta.arg;

                const parentNavigation = state.entities[parentSlug];

                if (!parentNavigation) return;

                const newPage = getDefaultItemState(slug);

                state.entities[slug] = newPage;

                const firstNonFixedItemIndex = parentNavigation.children.findIndex((child) => {
                    const childNavigation = state.entities[child];

                    if (!childNavigation) {
                        return false;
                    }

                    if (childNavigation.isFixed) {
                        return false;
                    }

                    return true;
                });

                const newChildIndex =
                    firstNonFixedItemIndex === -1
                        ? parentNavigation.children.length
                        : firstNonFixedItemIndex;

                addChild(parentNavigation, newPage, newChildIndex);
            }),
});

function updateEntity(
    state: NavigationTreeState,
    slug: string,
    entity: Partial<NavigationEntityState>,
) {
    state.entities[slug] = Object.assign(getDefaultItemState(slug), state.entities[slug], entity);
}

function setLoadingMore(
    state: NavigationTreeState,
    {type, value, parentSlug}: {type: 'next' | 'prev'; value: boolean; parentSlug: string},
) {
    const entity = state.entities[parentSlug];

    if (!entity) return;

    if (type === 'next') {
        entity.loadingNext = value;
    } else {
        entity.loadingPrev = value;
    }
}

const getDefaultItemState = (slug: string): NavigationEntityState => ({
    loading: false,
    loadingNext: false,
    loadingPrev: false,
    expandedState: NavigationItemExpandedState.Closed,
    children: [],
    hasChildren: false,
    type: NavigationPageType.Page,
    prevCursor: null,
    nextCursor: null,
    slug,
    isFixed: false,
});
